import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useMe } from '../hooks/useAuth.js';
import { SpinnerButton } from '../components/SpinnerButton.js';

export function WelcomePage() {
  const me = useMe();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const nextPath = params.get('next') ?? '/';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [accept, setAccept] = useState(false);

  const complete = useMutation({
    mutationFn: () =>
      api.auth.completeOnboarding({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        acceptTerms: true,
        acceptPrivacy: true,
        acceptCoverage: true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate(nextPath, { replace: true });
    },
  });

  if (me.isLoading) {
    return <div className="mx-auto max-w-md px-4 py-24 text-center text-navy-500">Loading…</div>;
  }
  if (!me.data?.user) {
    navigate('/login', { replace: true });
    return null;
  }
  if (me.data.user.onboardedAt) {
    navigate(nextPath, { replace: true });
    return null;
  }

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    accept &&
    !complete.isPending;

  return (
    <div className="mx-auto max-w-lg px-4 py-12">
      <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-2">
        Welcome to TowShare
      </p>
      <h1 className="text-3xl font-bold text-navy mb-2">A couple of quick details</h1>
      <p className="text-navy-600 mb-8">
        You're signed in as <span className="font-semibold text-navy">{me.data.user.email}</span>.
        Tell us your name and accept our policies and you're in.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) complete.mutate();
        }}
        className="card p-6 space-y-5"
      >
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="First name">
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="input"
              autoFocus
              required
              maxLength={60}
            />
          </Field>
          <Field label="Last name">
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="input"
              required
              maxLength={60}
            />
          </Field>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={accept}
            onChange={(e) => setAccept(e.target.checked)}
            className="mt-1 h-5 w-5 rounded border-navy-200 text-orange focus:ring-orange/30"
          />
          <span className="text-sm text-navy-700 leading-relaxed">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-orange hover:underline">
              Terms &amp; Conditions
            </Link>
            , the{' '}
            <Link to="/privacy" target="_blank" className="text-orange hover:underline">
              Privacy Policy
            </Link>
            , and I understand that TowShare's{' '}
            <Link to="/guarantee" target="_blank" className="text-orange hover:underline">
              $1,500 Trailer Guarantee
            </Link>{' '}
            sits on top of the renter's own auto insurance — which I'm responsible for
            confirming covers towing.
          </span>
        </label>

        {complete.isError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            Something went wrong. Please try again.
          </div>
        )}

        <SpinnerButton
          type="submit"
          disabled={!canSubmit}
          loading={complete.isPending}
          className="btn-primary w-full"
        >
          {complete.isPending ? 'Saving…' : 'Continue'}
        </SpinnerButton>
      </form>

      <p className="text-xs text-navy-400 mt-6 text-center">
        Already had an account? Your booking history is here — finish onboarding to access it.
      </p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-navy-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
